import React from 'react';
import Slider from 'react-slick';
import ChallengeCard from '../Cards/ChallengeCard';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import CTAButton from '../CTAButton';
import { GlobalEnquiryContext } from '../../App';
import { useContext } from 'react';

const Challenges = () => {
    const navigate = useNavigate();
    const { openEnquiry } = useContext(GlobalEnquiryContext);
    const challengeData = [
        {
            heading: "Delayed Hiring Cycles Are Slowing Business Momentum",
            problem: "Critical positions remain open for weeks or months due to unstructured recruitment workflows and inconsistent sourcing processes.",
            leadsTo: ["Delayed project execution", "Increased workload on internal teams", "Productivity slowdowns", "Missed business opportunities"],
            hiddenCost: "Every unfilled role creates operational pressure across departments."
        },
        {
            heading: "Generic Recruitment Processes Lead To Poor Hiring Quality",
            problem: (
                <>
                    Generic recruitment processes focus on volume instead of workforce alignment, often impacting candidate quality and <a href="https://www.greenhouse.com/uk/resources/glossary/what-is-employee-retention" target="_blank" rel="noreferrer" className="text-blue-600 underline">employee retention</a>.
                </>
            ),
            leadsTo: ["High attrition rates", "Weak candidate-role fit", "Repeated hiring cycles", "Reduced workforce productivity"],
            hiddenCost: "Bad hiring decisions impact performance, retention, and team stability."
        },
        {
            heading: "Workforce Scalability Becomes Difficult During Growth",
            problem: "Many companies grow faster than their hiring infrastructure can actually support.",
            leadsTo: ["Expansion bottlenecks", "Hiring inconsistencies", "Workforce instability", "Operational disruption during growth"],
            hiddenCost: "Scaling becomes chaotic without structured workforce systems."
        },
        {
            heading: "Payroll & Compliance Inefficiencies Create Hidden Risks",
            problem: "Manual payroll processes and disconnected compliance operations create unnecessary risk exposure.",
            leadsTo: ["Payroll inaccuracies", "Compliance inefficiencies", "Administrative overload", "Workforce documentation gaps"],
            hiddenCost: "Operational errors become expensive as workforce size increases."
        },
        {
            heading: "Internal HR Teams Are Overburdened By Complexity",
            problem: "Internal HR teams are forced to manage recruitment, payroll, onboarding, and administration simultaneously.",
            leadsTo: ["Recruitment delays", "HR burnout", "Reduced operational efficiency", "Lack of strategic focus"],
            hiddenCost: "Internal teams spend more time managing processes than driving growth."
        }
    ];

    const settings = {
        dots: true,
        arrows: false,
        infinite: true,
        speed: 1000,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        responsive: [
            { breakpoint: 1150, settings: { slidesToShow: 2 } },
            { breakpoint: 750, settings: { slidesToShow: 1 } }
        ]
    };

    return (
        <section className="py-10" style={{ backgroundColor: '#ffffff' }}>
            <div className="container mx-auto px-6">
                <div className="mb-10">
                    <h2 className="text-[#01014C] text-3xl md:text-5xl font-bold syne leading-tight">
                        Navigating Core <br />
                        <span className="text-[#38bdf8]">Workforce Challenges</span>
                    </h2>
                    <p className="text-slate-700 text-sm md:text-lg max-w-5xl mt-4 font-medium">
                        Discover expert perspectives on the key issues shaping modern workplaces and learn how HR leaders can drive effective, people-centric solutions.
                    </p>
                </div>

                <Slider {...settings} className="challenge-slider pb-">
                    {challengeData.map((item, index) => (
                        <ChallengeCard key={index} data={item} index={index} />
                    ))}
                </Slider>

                <div className="mt-12 flex justify-center">
                    <CTAButton 
                        text="Discuss Hiring Challenges"
                        onClick={() => openEnquiry("Discuss Your Hiring Challenges", [])}
                        type="secondary"
                    />
                </div>
            </div>
        </section>
    );
};



export default Challenges;